import { useRef } from 'react';
import { Inline, Tooltip, breakpoints, vars, Stack } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { css, cx, keyframes } from '@eti/styles';
import { analytics, price as priceUtils } from '@eti/utils';
import { Info } from '@phosphor-icons/react';
import { resetButtonStyles } from '../utils/summaryStyles';
import { SEATING_PREFERENCE_OPTIONS } from '../../../utils/constants';
import UnavailableRow from './SummaryUnavailableRow';
import TravelerRow from './SummaryTravelerRow';
import SegmentRow from './SummarySegmentRow';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const wrapperStyles = css`
  height: 100%;
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;

  @media (min-width: ${breakpoints._768}) {
    margin: 0 1.875rem;
  }
`;
const tableStyles = css`
  display: table;
  margin-bottom: 60px;
  table-layout: fixed;
  width: 100%;

  @media (min-width: ${breakpoints._768}) {
    margin-bottom: 102px;
  }
`;
const tableBodyStyles = css`
  @media (max-width: 47.9375em) {
    display: block;
  }
`;
const totalPriceStyles = css`
  font-size: 1rem;
  font-weight: 600;
`;
const mainWrapper = css`
  animation: ${fadeIn} 1s ease;
  height: 83%;
  margin-top: 30px;
  overflow: hidden;
  padding-bottom: 60px;
  width: 100%;
`;
const priceWrapperStyles = css`
  margin-bottom: 14px;
  padding-inline: 12px;

  @media (min-width: ${breakpoints._768}) {
    justify-content: flex-end;
    padding-inline: 14px;
  }
`;
const endAlignedStyles = css`
  display: flex;
  margin-inline-start: auto;

  @media (min-width: ${breakpoints._768}) {
    margin-inline-start: 0;
  }
`;
const infoIconButtonStyles = css`
  border: 2px solid transparent;
  border-radius: 50%;
  cursor: pointer;
  display: flex;

  &:focus-visible {
    border: 2px solid ${vars.colors.focusRing};
    transition: all 0.2s ease;
  }

  @media (min-width: ${breakpoints._768}) {
    cursor: default;
    margin-top: 0;
  }
`;
const getSelectedSeat = (id, selectedSeats) => selectedSeats === null || selectedSeats === void 0 ? void 0 : selectedSeats.find(seat => Number(seat.id) === id);
const getTravelers = (travelers, ids) => travelers.filter(traveler => ((traveler === null || traveler === void 0 ? void 0 : traveler.id) && (ids === null || ids === void 0 ? void 0 : ids.indexOf(traveler.id))) !== -1);
const isDeckAvailble = deck => Boolean(deck && deck.cabins && deck.cabins.length > 0);
const isAvailable = segment => {
  const {
    mainDeck,
    lowerDeck,
    upperDeck
  } = segment;
  const isUpperDeckAvailable = isDeckAvailble(upperDeck);
  const isMainDeckAvailable = isDeckAvailble(mainDeck);
  const isLowerDeckAvailable = isDeckAvailble(lowerDeck);
  return Boolean(isMainDeckAvailable || isLowerDeckAvailable || isUpperDeckAvailable);
};
const renderTravelers = (travelers, boundIndex, segmentIndex, boundSegmentIndex, getSelected, onToggleSeatSelect, onRemoveSeat, carrier, isRyanairInTrip) => travelers.map((_ref, travelerIndex) => {
  let {
    firstName,
    ageType,
    id
  } = _ref;
  return /*#__PURE__*/_jsx(TravelerRow, {
    ageType: ageType,
    boundIndex: boundIndex,
    carrier: carrier,
    isRyanairInTrip: isRyanairInTrip,
    name: firstName || '',
    onRemoveSeat: onRemoveSeat(boundSegmentIndex, id),
    onToggleSeatSelect: onToggleSeatSelect(boundSegmentIndex, id),
    segmentIndex: segmentIndex,
    selectedSeat: getSelectedSeat(id, getSelected(boundSegmentIndex)),
    travelerIndex: travelerIndex
  }, id);
});
const renderSegments = (travelers, getSelected, onToggleSeatSelect, onRemoveSeat, isRyanairInTrip) => segment => {
  const {
    from,
    boundIndex,
    segmentIndex,
    boundSegmentIndex,
    to,
    travelerIds,
    carrier,
    __typename: type
  } = segment;
  const isSegmentAvailable = isAvailable(segment);
  const isSeatPreferenceAvailable = type === SEATING_PREFERENCE_OPTIONS.SELL_SEATING_BESIDE || type === SEATING_PREFERENCE_OPTIONS.SELL_SEATING_PREFERENCE;
  return [/*#__PURE__*/_jsx(SegmentRow, {
    destination: to,
    isAvailable: isSegmentAvailable || isSeatPreferenceAvailable,
    isOutbound: boundIndex === 0,
    origin: from
  }, `${boundSegmentIndex}`), isSegmentAvailable ? [renderTravelers(getTravelers(travelers, travelerIds || []), boundIndex, segmentIndex, boundSegmentIndex, getSelected, onToggleSeatSelect, onRemoveSeat, carrier, isRyanairInTrip)] : /*#__PURE__*/_jsx(UnavailableRow, {
    boundIndex: boundIndex,
    onRemoveOption: onRemoveSeat(boundSegmentIndex, 0),
    onToggleOptionSelect: onToggleSeatSelect(boundSegmentIndex, 0),
    segmentIndex: segmentIndex,
    selected: getSelected(boundSegmentIndex),
    type: type || ''
  }, `${boundIndex}-${segmentIndex}-unavailable`)];
};
const SeatSummary = _ref2 => {
  let {
    segments = [],
    getSelected = () => [],
    onRemoveSeat = () => () => {},
    onToggleSeatSelect = () => () => {},
    totalMarkup,
    totalPrice,
    travelers = []
  } = _ref2;
  const {
    t
  } = useTranslation();
  const {
    p
  } = useProperty();
  const totalPriceLabel = t('Product.SeatMap.Summary.TotalPrice');
  const markupLabel = t('SeatMap.PriceInformation.Markup');
  const priceLabel = t('SeatMap.PriceInformation.Price');
  const providerCostLabel = t('SeatMap.PriceInformation.ProviderCost');
  const targetRef = useRef(null);
  const showToolTip = Number.isInteger(totalMarkup);
  const ryanairCarrierCode = p('IbeClient.RyanairMarketingCarriers');
  const lowerCaseRyanAirCarrierCodes = ryanairCarrierCode === null || ryanairCarrierCode === void 0 ? void 0 : ryanairCarrierCode.map(code => code.toLowerCase());
  const isRyanairInTrip = segments.some(segment => {
    var _segment$carrier;
    return lowerCaseRyanAirCarrierCodes === null || lowerCaseRyanAirCarrierCodes === void 0 ? void 0 : lowerCaseRyanAirCarrierCodes.includes((_segment$carrier = segment.carrier) === null || _segment$carrier === void 0 || (_segment$carrier = _segment$carrier.code) === null || _segment$carrier === void 0 ? void 0 : _segment$carrier.toLowerCase());
  });
  analytics.useGADataLayerEffect({
    event: 'seatingVisible',
    seatingSummary: 'yes'
  });
  return /*#__PURE__*/_jsx(Stack, {
    className: mainWrapper,
    "data-testid": "seatMap-summaryTable",
    children: /*#__PURE__*/_jsxs("div", {
      className: wrapperStyles,
      ref: targetRef,
      children: [/*#__PURE__*/_jsx("table", {
        className: tableStyles,
        children: /*#__PURE__*/_jsx("tbody", {
          className: tableBodyStyles,
          children: segments === null || segments === void 0 ? void 0 : segments.map(renderSegments(travelers, getSelected, onToggleSeatSelect, onRemoveSeat, isRyanairInTrip))
        })
      }), /*#__PURE__*/_jsxs(Inline, {
        className: priceWrapperStyles,
        "data-testid": "seatMap-priceWrapper",
        spacing: 8,
        children: [/*#__PURE__*/_jsx("span", {
          children: totalPriceLabel
        }), /*#__PURE__*/_jsxs(Inline, {
          alignY: "center",
          className: endAlignedStyles,
          spacing: 4,
          children: [showToolTip && !isRyanairInTrip && /*#__PURE__*/_jsx(Tooltip, {
            content: /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsxs("div", {
                "data-testid": "seatMap-toolTip-markupLabel",
                children: [markupLabel, ": ", priceUtils.formatPrice(totalMarkup)]
              }), /*#__PURE__*/_jsxs("div", {
                "data-testid": "seatMap-toolTip-providerCostLabel",
                children: [providerCostLabel, ":", ' ', priceUtils.formatPrice(totalPrice - Number(totalMarkup))]
              }), /*#__PURE__*/_jsxs("div", {
                "data-testid": "seatMap-toolTip-totalPriceLabel",
                children: [totalPriceLabel, ": ", priceUtils.formatPrice(totalPrice)]
              })]
            }),
            "data-testid": "seatMap-summary-price-tooltip",
            title: priceLabel,
            children: /*#__PURE__*/_jsx("button", {
              "aria-label": priceLabel,
              className: cx(resetButtonStyles, infoIconButtonStyles),
              "data-testid": "seatMap-informationIcon-button",
              type: "button",
              children: /*#__PURE__*/_jsx(Info, {
                color: vars.colors.signal.information.foreground,
                size: 20,
                weight: "fill"
              })
            })
          }), /*#__PURE__*/_jsx("span", {
            className: totalPriceStyles,
            "data-testid": "seatMap-totalPrice",
            children: priceUtils.formatPrice(totalPrice)
          })]
        })]
      })]
    })
  });
};
export default SeatSummary;
//# sourceMappingURL=SeatSummary.js.map